import {
  AuthAction,
  AuthState,
  FetchMeRequestAction,
  FetchMeSuccessAction,
  LoginMfaAction,
  UpdateCurrentUserPictureAction,
  SetThirdPartySSOAction,
  UpdateCurrentUserPreferencesRequestAction,
  UpdateCurrentUserPreferencesSuccessAction,
} from './types';
import {
  FETCH_ME_FAILURE,
  FETCH_ME_REQUEST,
  FETCH_ME_SUCCESS,
  FETCH_THIRD_PARTY_SSO_URL_FAILURE,
  FETCH_THIRD_PARTY_SSO_URL_REQUEST,
  FETCH_THIRD_PARTY_SSO_URL_SUCCESS,
  LOGIN_FAILURE,
  LOGIN_MFA,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGOUT_FAILURE,
  LOGOUT_REQUEST,
  LOGOUT_SUCCESS,
  RESET_PASSWORD_EMAIL_FAILURE,
  RESET_PASSWORD_EMAIL_REQUEST,
  RESET_PASSWORD_EMAIL_SUCCESS,
  SET_THIRD_PARTY_SSO,
  UPDATE_CURRENT_USER_PICTURE,
  UPDATE_CURRENT_USER_PREFERENCES_FAILURE,
  UPDATE_CURRENT_USER_PREFERENCES_REQUEST,
  UPDATE_CURRENT_USER_PREFERENCES_SUCCESS,
} from './constants';
import { UserLicence } from './models';
import { LicenseType } from 'store/app/enums';
import { UserLicenceResponse } from './api';

export const initialState: AuthState = {
  loading: false,
  user: undefined,
  mfaResponse: undefined,
  userLicence: {
    licenseDaysLeft: 0,
    licenseType: LicenseType.BLOCKED,
    licenceTypeId: '',
    licenceExpireDate: undefined,
    maxVatCheckers: 0,
    maxConnectedEntity: 0,
    status: null,
  },
  thirdPartySSO: undefined,
  preferencesSaving: false,
};

export const authReducer = (
  state = initialState,
  action: AuthAction
): AuthState => {
  switch (action.type) {
    case LOGIN_REQUEST:
    case LOGOUT_REQUEST:
    case RESET_PASSWORD_EMAIL_REQUEST:
    case FETCH_THIRD_PARTY_SSO_URL_REQUEST:
      return handleRequest(state);
    case FETCH_ME_REQUEST:
      return handleFetchMeRequest(state, action as FetchMeRequestAction);
    case LOGIN_SUCCESS:
    case RESET_PASSWORD_EMAIL_SUCCESS:
    case FETCH_THIRD_PARTY_SSO_URL_SUCCESS:
      return handleSuccess(state);
    case FETCH_ME_SUCCESS:
      return handleFetchMeSuccess(state, action);
    case LOGOUT_SUCCESS:
      return handleLogoutSuccess();
    case LOGIN_FAILURE:
    case FETCH_ME_FAILURE:
    case FETCH_THIRD_PARTY_SSO_URL_FAILURE:
    case LOGOUT_FAILURE:
    case RESET_PASSWORD_EMAIL_FAILURE:
      return handleFailure(state);
    case LOGIN_MFA:
      return handleLoginMfa(state, action);
    case UPDATE_CURRENT_USER_PICTURE:
      return handleUpdateCurrentUserPicture(state, action);
    case SET_THIRD_PARTY_SSO:
      return handleSetThirdPartySSO(state, action);
    case UPDATE_CURRENT_USER_PREFERENCES_REQUEST:
      return handleUpdateCurrentUserPreferencesRequest(state, action);
    case UPDATE_CURRENT_USER_PREFERENCES_SUCCESS:
      return handleUpdateCurrentUserPreferencesSuccess(state, action);
    case UPDATE_CURRENT_USER_PREFERENCES_FAILURE:
      return {
        ...state,
        preferencesSaving: false,
      };
    default:
      return state;
  }
};

function handleFetchMeSuccess(
  state: AuthState,
  action: FetchMeSuccessAction
): AuthState {
  const userLicence: UserLicence = action.payload.userLicence
    ? checkLicence(action.payload.userLicence)
    : state.userLicence;
  return {
    ...state,
    loading: false,
    user: action.payload.user,
    userLicence,
  };
}

function handleLoginMfa(state: AuthState, action: LoginMfaAction): AuthState {
  return {
    ...state,
    loading: false,
    mfaResponse: action.payload.mfaResponse,
  };
}

function handleLogoutSuccess(): AuthState {
  return {
    ...initialState,
    loading: false,
  };
}

function handleRequest(state: AuthState): AuthState {
  return {
    ...state,
    loading: true,
    user: undefined,
  };
}

/**
 * Handle FETCH_ME_REQUEST separately to support session checks.
 * When isSessionCheck is true, we don't change loading state or clear user
 * to prevent UI flashing during background session verification.
 */
function handleFetchMeRequest(
  state: AuthState,
  action: FetchMeRequestAction
): AuthState {
  const isSessionCheck = action.payload?.isSessionCheck ?? false;

  // For session checks, don't modify state at all - just verify silently
  if (isSessionCheck) {
    return state;
  }

  // For regular fetchMe requests, proceed with normal loading behavior
  return {
    ...state,
    loading: true,
    user: undefined,
  };
}

function handleUpdateCurrentUserPreferencesRequest(
  state: AuthState,
  action: UpdateCurrentUserPreferencesRequestAction
): AuthState {
  const { preferences } = action.payload;
  return {
    ...state,
    preferencesSaving: true,
    user: state.user
      ? {
          ...state.user,
          prefDetails: [state.user.prefDetails?.[0] ?? '', preferences],
        }
      : state.user,
  };
}

function handleUpdateCurrentUserPreferencesSuccess(
  state: AuthState,
  action: UpdateCurrentUserPreferencesSuccessAction
): AuthState {
  const { preferences } = action.payload;
  let newUser: any;
  if (state.user) {
    newUser = {
      ...state.user,
      prefDetails: [state.user.prefDetails?.[0], preferences],
    };
  } else {
    newUser = undefined;
  }
  return {
    ...state,
    loading: false,
    preferencesSaving: false,
    user: newUser,
  };
}

function handleSuccess(state: AuthState): AuthState {
  return {
    ...state,
    loading: false,
  };
}

function handleFailure(state: AuthState): AuthState {
  return {
    ...state,
    loading: false,
    user: undefined,
  };
}

function handleUpdateCurrentUserPicture(
  state: AuthState,
  action: UpdateCurrentUserPictureAction
): AuthState {
  const profilePicture = action.payload.picture;
  let newUser: any;
  if (state.user) {
    newUser = { ...state.user, profilePicture };
  } else {
    newUser = undefined;
  }

  return {
    ...state,
    loading: false,
    user: newUser,
  };
}

const checkLicence = (licenseResponse: UserLicenceResponse) => {
  let licenseType: LicenseType = LicenseType.BLOCKED;
  // Convert string dates to Date objects for proper comparison
  const expireDate = new Date(licenseResponse.expire_date);
  const nextExpireDate = new Date(licenseResponse.next_expire_date);
  // First check if all three dates exist
  if (
    licenseResponse.expire_date &&
    licenseResponse.expire_trial &&
    licenseResponse.next_expire_date
  ) {
    // If all three dates have the same value, it's a trial
    if (
      licenseResponse.expire_date === licenseResponse.expire_trial &&
      licenseResponse.expire_trial === licenseResponse.next_expire_date
    ) {
      licenseType = LicenseType.TRIAL;
    }
    // If they don't have the same value, check if expiry date is larger than next expiry date
    else if (expireDate > nextExpireDate) {
      // Check if the expiry date is in the future
      const todayDate = new Date();
      if (expireDate > todayDate) {
        licenseType = LicenseType.PAID;
      } else {
        // If expiry date has passed, license type is blocked
        licenseType = LicenseType.BLOCKED;
      }
    }
    // Any other scenario, license type remains blocked
    else {
      licenseType = LicenseType.BLOCKED;
    }
  }
  // If any of the three dates don't exist, license type remains blocked

  const userLicence: UserLicence = {
    licenseDaysLeft: calcDaysLeft(licenseResponse),
    licenseType,
    licenceTypeId: licenseResponse.typeId,
    licenceExpireDate: expireDate,
    maxVatCheckers: licenseResponse.maxVatCheckers,
    maxConnectedEntity: licenseResponse.maxConnectedEntity,
    status: licenseResponse.status,
  };
  return userLicence;
};

const calcDaysLeft = (licenseResponse: UserLicenceResponse) => {
  if (!licenseResponse?.next_expire_date) return 0;

  const expiry = new Date(licenseResponse.next_expire_date);
  const today = new Date();

  expiry.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diff = expiry.getTime() - today.getTime();
  const daysFraction = diff / (1000 * 3600 * 24);

  return diff > 0 ? Math.trunc(daysFraction) : 0;
};

function handleSetThirdPartySSO(
  state: AuthState,
  action: SetThirdPartySSOAction
): AuthState {
  return {
    ...state,
    loading: false,
    thirdPartySSO: action.payload.thirdPartySSO,
  };
}
